import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useListView } from '../hooks/useListView';
import { ListRow, ListHeaderRow } from './ListRow';
import { ListInfiniteScroll } from './ListInfiniteScroll';
import { Pagination } from '@/shared/components/custom/pagination';
import { cn } from '@/core/lib/utils';
import type { ListViewPropsAll, ListViewProps } from '../types';
import type { DataSourceResult } from '@/shared/hooks/useDataSource.types';

function ListViewComponent<T>(props: ListViewPropsAll<T>): ReactNode {
  const { t } = useTranslation('listView');
  const {
    data,
    dataSource,
    columns,
    features,
    getRowId,
    className,
    defaultGrouping = [],
    defaultSorting = [],
    defaultVisibility = {},
    editingRowId,
    meta,
    sorting,
    onSortingChange,
    manualSorting,
    pagination,
  } = props as ListViewProps<T> & { data?: T[]; dataSource?: DataSourceResult<T> };

  const dataToUse = data ?? dataSource?.data ?? [];

  const { table } = useListView(dataToUse, {
    columns,
    getRowId,
    defaultGrouping,
    defaultSorting,
    defaultVisibility,
    meta,
    sorting,
    onSortingChange,
    manualSorting,
  });

  useEffect(() => {
    features?.forEach((f) => f.onInit?.(table));
  }, []);

  const hasGrouping = table.getState().grouping.length > 0;
  const rows = hasGrouping ? table.getGroupedRowModel().rows : table.getRowModel().rows;

  const toolbarNodes = features?.map((f) => f.renderToolbar?.({ table })).filter(Boolean);

  const testId = (props as unknown as Record<string, string | undefined>)['data-testid'];

  return (
    <>
      <div
        className={cn('flex flex-col bg-background rounded-lg border border-border', className)}
        data-testid={testId ?? 'inline-table-root'}
      >
        {toolbarNodes && toolbarNodes.length > 0 && (
          <div className="flex items-center px-4 py-2 border-b border-border gap-2">
            {toolbarNodes}
          </div>
        )}

        <ListHeaderRow headers={table.getFlatHeaders()} />

        <div className="flex-1 overflow-auto">
          {rows.length === 0 ? (
            <div
              className="flex items-center justify-center py-12 text-muted-foreground"
              data-testid="inline-table-empty"
            >
              {t('empty')}
            </div>
          ) : (
            rows.map((row) => <ListRow key={row.id} row={row} editingRowId={editingRowId} />)
          )}
        </div>

        {dataSource?.mode === 'infinite' && (
          <ListInfiniteScroll
            hasNextPage={dataSource.hasNextPage ?? false}
            fetchNextPage={dataSource.fetchNextPage ?? (() => {})}
            isFetching={dataSource.isFetching}
          />
        )}
      </div>

      {pagination && <Pagination {...pagination} />}
    </>
  );
}

export const ListView = ListViewComponent;
